import {
  abs,
  atan,
  Discard,
  dot,
  exp,
  Fn,
  float,
  floor,
  If,
  int,
  length,
  max,
  min,
  mix,
  mx_noise_float,
  normalize,
  normalView,
  normalWorld,
  positionWorld,
  smoothstep,
  tan,
  uniform,
  uniformArray,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { FACE_N, FACE_U, FACE_V } from '../../terrain/cubeSphere';
import { lipDepthNode, RIM_SHADE, type TerrainMouth, WORN_SLOPE } from '../../terrain/TerrainMouth';
import { paletteHeight, type TerrainProfile } from '../../terrain/TerrainProfile';
import { type SurfaceContext, type TerrainStyle, vec3When } from '../../terrain/TerrainStyle';
import { type InteriorLook, interiorSurface, lookNode } from './caveInteriorMaterial';

type N = any;

/** A hollow's ground as its terrain paints it: the same palette, fed the same inputs. */
export interface CaveGround {
  style: TerrainStyle;
  profile: TerrainProfile;
  /** The hollow's sea-level radius (m): heights are measured in from it. */
  radius: number;
  /** The terrain's clock, for styles with moving surfaces. */
  time: N;
}

/** One end of a cave, where it opens into a hollow. */
export interface CaveEnd {
  /** Hollow centre relative to the camera (refresh every frame). */
  centerRel: { value: THREE.Vector3 } & N;
  /** Radius of the mouth's plateau (R − height). */
  plateauRadius: number;
  /** The mouth: the tunnel is clipped at its ground (the plateau and its lip). */
  mouth: TerrainMouth;
  /** The ground around the mouth, whose look runs on into the first metres of the tunnel. */
  ground: CaveGround;
  /** Light spilling in through this mouth, fading over the first metres of rock. */
  leak: THREE.Color;
  /** How much of its full daylight the hollow has at this mouth now (0 night … 1 day; refresh). */
  light: { value: number } & N;
  /** Colour of the glowing lichen near this end. */
  glow: THREE.Color;
  /** The hollow's daylight (irradiance) on a surface with normal `n` at hollow-local `local`. */
  daylight: (local: N, n: N) => N;
  /**
   * The ground mesh as drawn at the mouth now (refresh every frame; see meshGroundNode): the
   * cells per cube-face edge of the level drawn there (its vertices sit on every other texel
   * of the tiles), and how far it has morphed toward its parent level (0..1).
   */
  groundCells: { value: number } & N;
  groundMorph: { value: number } & N;
}

/**
 * Below the ground at a mouth (m): the hollow's own ground runs on into the tunnel to
 * GROUND_FROM, then gives way to the cave's rock in ragged patches, gone by GROUND_TO.
 */
const GROUND_FROM = 1.5;
const GROUND_TO = 9;
/**
 * Daylight on the rock: at the rim the same share as the ground at the hole's edge
 * (RIM_SHADE), dimming to nothing this far in (m) as the walls hide the sky.
 */
export const DAYLIGHT_DEPTH = 6;
/**
 * Right at the rim the rock is lit as the ground beside it (facing up), turning to its own
 * facing over this depth (m). The collar standing above the ground faces sideways: with the sun
 * overhead and nothing else, it showed as a dark line around the hole.
 */
const RIM_LIGHT_DEPTH = 1;
/**
 * The tunnel runs on this far above the ground at each mouth (m): a collar a few centimetres
 * high that the ground's cut edge rests against (see the clip below).
 */
const RIM_COLLAR = 0.04;
/** Near the ground (m), the tube is clipped at the ground as its triangles draw it (see meshGroundNode). */
const MESH_BAND = 0.6;

/** Cube-face axes for the GPU: N, U, V of each face (see cubeSphere). */
const FACE_AXES: N = uniformArray(
  FACE_N.flatMap((n, f) => [n.clone(), FACE_U[f].clone(), FACE_V[f].clone()]),
  'vec3',
);

/**
 * The ground under unit direction `down` as the terrain's triangles draw it near a mouth:
 * the plateau and its lip sampled at the drawn level's vertices (a regular grid in cube-face
 * coordinates) and interpolated over the same two triangles per cell (split from (i+1, j) to
 * (i, j+1)); while that level morphs, blended with its parent's the same way (the finer
 * triangles nest in the coarser ones). Returns its distance from the hollow's centre.
 *
 * The triangles can't follow the lip: on the approach side its ramp meets a level shelf (the
 * plateau point above the tube lies in the hole, where the lip is full depth), and where the
 * lip turns from the sides to full depth it curves; triangles across those cut the corner, up
 * to 12 cm above the true ground in the Geodes, whose ground vertices are a metre apart, and
 * far more on a coarse tile that hasn't refined yet. A tube clipped at the true ground stopped
 * short of the drawn ground's edge, and the gap showed the background. Clipped at this, the two
 * meet within the collar.
 */
function meshGroundNode(e: CaveEnd, down: N): N {
  const fine = drawnGround(e, down, e.groundCells);
  return Fn(() => {
    const g = fine.toVar();
    If(e.groundMorph.greaterThan(0), () => {
      g.assign(mix(fine, drawnGround(e, down, e.groundCells.mul(0.5)), e.groundMorph));
    });
    return g;
  })();
}

/** The plateau and lip on a grid of `cells` cells per face edge, as its triangles draw it. */
function drawnGround(e: CaveEnd, down: N, cells: N): N {
  const m = e.mouth;
  // Which face (the largest component), and the direction's (u, v) on it.
  const ax = abs(down.x);
  const ay = abs(down.y);
  const az = abs(down.z);
  const onX = ax.greaterThanEqual(ay).and(ax.greaterThanEqual(az));
  const onY = onX.not().and(ay.greaterThanEqual(az));
  const face = onX
    .select(
      down.x.greaterThan(0).select(int(0), int(1)),
      onY.select(down.y.greaterThan(0).select(int(2), int(3)), down.z.greaterThan(0).select(int(4), int(5))),
    )
    .toVar();
  const Nf = FACE_AXES.element(face.mul(3));
  const Uf = FACE_AXES.element(face.mul(3).add(1));
  const Vf = FACE_AXES.element(face.mul(3).add(2));
  const nd = dot(down, Nf);
  const k = 4 / Math.PI;
  const half = cells.mul(0.5);
  const gu = atan(dot(down, Uf).div(nd)).mul(k).add(1).mul(half);
  const gv = atan(dot(down, Vf).div(nd)).mul(k).add(1).mul(half);
  const iu = floor(gu);
  const iv = floor(gv);
  const fu = gu.sub(iu);
  const fv = gv.sub(iv);
  // The ground at a grid vertex: the plateau and the lip, as the tile generator samples it.
  const at = (du: number, dv: number) => {
    const u = iu.add(du).div(half).sub(1);
    const v = iv.add(dv).div(half).sub(1);
    const q = Nf.add(Uf.mul(tan(u.mul(Math.PI / 4)))).add(Vf.mul(tan(v.mul(Math.PI / 4))));
    return lipDepthNode(m, e.plateauRadius, normalize(q));
  };
  const la = at(0, 0);
  const lb = at(1, 0);
  const lc = at(0, 1);
  const ld = at(1, 1);
  const lip = fu
    .add(fv)
    .lessThanEqual(1)
    .select(
      la.add(lb.sub(la).mul(fu)).add(lc.sub(la).mul(fv)),
      ld.add(lc.sub(ld).mul(float(1).sub(fu))).add(lb.sub(ld).mul(float(1).sub(fv))),
    );
  // The ground mesh never dips below sea level (water lies there).
  return min(lip.add(e.plateauRadius), e.ground.radius);
}
/**
 * Below a mouth (m): the entrance's rock gives way to the interior's (see interiorSurface),
 * so the two are the same where they meet at the gate, whatever the tube does there.
 */
const INTERIOR_FROM = 10;
const INTERIOR_TO = 18;

/** The interior an entrance leads to: its look takes over down the entrance. */
export interface EntranceInterior extends InteriorLook {
  /** The look (rgb tint, wetness) of the interior's passage at the gates. */
  look: readonly [number, number, number, number];
}

/**
 * Rock for a cave between two hollows. Layered stone, lit by the player's lantern
 * (plus whatever `lightsNode` holds), light leaking in at each mouth, and patches
 * of glowing lichen that thicken and change colour toward the far end, so the way
 * on is visible even without a lantern. Fragments inside either hollow are
 * discarded, cutting the tube exactly where it meets the ground at the mouth.
 * At each mouth the walls start out as that hollow's ground (its own palette, as the
 * worn ground at the rim) in its daylight, and turn into the cave's rock over the next metres.
 * With `interior`, deeper down they turn into the interior's rock.
 */
export function createCaveMaterial(
  ends: [CaveEnd, CaveEnd],
  lightsNode: THREE.LightsNode,
  glowAmount = 1,
  interior?: EntranceInterior,
) {
  const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.86, metalness: 0 });
  mat.lightsNode = lightsNode;
  const [a, b] = ends;
  const leakA = uniform(a.leak);
  const leakB = uniform(b.leak);
  const glowA = uniform(a.glow);
  const glowB = uniform(b.glow);

  // Depth below each mouth's ground, along the line from its hollow's centre.
  const end = (e: CaveEnd) => {
    const local = positionWorld.sub(e.centerRel);
    const r = length(local);
    const down = local.div(max(r, 1));
    const ground = lipDepthNode(e.mouth, e.plateauRadius, down).add(e.plateauRadius);
    // Where the clip is decided (near the ground), the ground as its triangles draw it.
    const drawn = Fn(() => {
      const g = ground.toVar();
      If(abs(r.sub(ground)).lessThan(MESH_BAND), () => {
        g.assign(meshGroundNode(e, down));
      });
      return g;
    })();
    return { local, r, down, ground, drawn, depth: max(r.sub(ground), 0) };
  };
  const A = end(a);
  const B = end(b);
  // 0 at A's mouth … 1 at B's.
  const along = A.depth.div(max(A.depth.add(B.depth), 1e-3));

  const p = A.local;
  const n1 = mx_noise_float(p.mul(0.21));
  const n2 = mx_noise_float(p.mul(1.3));
  const n3 = mx_noise_float(p.mul(4.3));
  // Blotchy stone: large patches, broken up by finer grain.
  const tone = smoothstep(-0.55, 0.6, n1.mul(0.8).add(n2.mul(0.35)));
  const rock: N = mix(vec3(0.16, 0.135, 0.12), vec3(0.37, 0.32, 0.27), tone).mul(
    n2.mul(0.12).add(n3.mul(0.07)).add(0.93),
  );

  // Each end's ground: all of it at the rim, the cave's rock showing through deeper in.
  const ragged = n1.mul(2.2).add(n2.mul(0.8));
  const groundShare = (E: ReturnType<typeof end>) =>
    float(1).sub(smoothstep(GROUND_FROM, GROUND_TO, E.depth.add(ragged)));
  const wA = groundShare(A);
  const wB = groundShare(B);
  // The palette's inputs, as the terrain computes them. Vars, so both ends' branches share them.
  const dist = length(positionWorld).toVar();
  const near = float(1)
    .sub(smoothstep(25, 220, dist))
    .toVar();
  const mid = float(1)
    .sub(smoothstep(300, 2500, dist))
    .toVar();
  const context = (e: CaveEnd, E: ReturnType<typeof end>): SurfaceContext => {
    const g = e.ground;
    const up = E.down.negate().toVar();
    // Walls, floor and roof alike are the worn ground at the rim (not cliffs, which many
    // palettes paint quite differently): the ground runs on over the edge unchanged.
    const slope = float(WORN_SLOPE);
    // Heights run on from the rim's, but no lower: no beaches or snow lines underground.
    const h = paletteHeight(
      g.profile,
      max(float(g.radius).sub(E.r), e.mouth.height - e.mouth.lipDepth),
    ).toVar();
    return { p: E.local, h, slope, up, dist, near, mid, profile: g.profile, time: g.time };
  };
  const ctxA = context(a, A);
  const ctxB = context(b, B);
  // The palette only runs (a real branch) where its share isn't zero: most of a cave is bare rock.
  const land = (e: CaveEnd, ctx: SurfaceContext, w: N, part: 'albedo' | 'emissive') =>
    vec3When(() => e.ground.style.land(ctx)[part] ?? vec3(0), w);
  const albedo: N = mix(mix(rock, land(a, ctxA, wA, 'albedo'), wA), land(b, ctxB, wB, 'albedo'), wB);

  // Lichen: sparse near A, thick near B, in B's colour past the middle. It grows on the
  // cave's rock, not on the hollows' ground.
  const density = mix(float(0.12), float(1), smoothstep(0.1, 0.85, along));
  const spotN = mx_noise_float(p.mul(3.1)).add(n3.mul(0.25));
  const edge = float(0.68).sub(density.mul(0.24));
  const inRock = float(1).sub(wA).mul(float(1).sub(wB));
  const spots = smoothstep(edge, edge.add(0.05), spotN).mul(inRock);
  const glowCol = mix(vec3(glowA as N), vec3(glowB as N), smoothstep(0.35, 0.65, along));
  const lichen = glowCol.mul(spots.mul(glowAmount));
  // A little of the lichen's light on the bare rock around it.
  const bounce = albedo.mul(glowCol).mul(density.mul(inRock).mul(0.14 * glowAmount));
  // Light from each mouth. At the rim the rock has the hollow's own daylight, as the ground by
  // the hole gets it (E · albedo / π, the same share as the ground at the hole's edge). Deeper
  // in it hands over to the cave's light spilling in (the mouth's own colour, fading with the
  // day there) as the walls hide the sky; the two cross over so the light only ever fades.
  const under = (E: ReturnType<typeof end>) => smoothstep(0, DAYLIGHT_DEPTH, E.depth);
  const uA = under(A);
  const uB = under(B);
  const litNormal = (E: ReturnType<typeof end>) => {
    const n = mix(E.down.negate(), normalWorld, smoothstep(0, RIM_LIGHT_DEPTH, E.depth));
    // (A roof facing down can cancel the blend out; that point is left without sun.)
    return n.div(max(length(n), 1e-4));
  };
  const daylight = a
    .daylight(A.local, litNormal(A))
    .mul(float(1).sub(uA).mul(RIM_SHADE))
    .add(b.daylight(B.local, litNormal(B)).mul(float(1).sub(uB).mul(RIM_SHADE)));
  const leak = vec3(leakA as N)
    .mul(exp(A.depth.div(-6)).mul(uA).mul(a.light))
    .add(vec3(leakB as N).mul(exp(B.depth.div(-6)).mul(uB).mul(b.light)));
  const surface = albedo.mul(float(1).sub(spots.mul(0.5)));

  // Deep down an entrance, the interior's rock.
  const inner = interior ? smoothstep(INTERIOR_FROM, INTERIOR_TO, min(A.depth, B.depth)) : float(0);
  const deep = interior ? interiorSurface(interior, lookNode(interior.look)) : null;
  const color: N = deep ? mix(surface, deep.albedo, inner) : surface;
  if (deep) {
    mat.roughnessNode = mix(float(0.86), deep.roughness, inner);
    mat.normalNode = normalize(mix(normalView, deep.normal, inner));
  }

  mat.colorNode = Fn(() => {
    // Clipped just above the ground as it is drawn (RIM_COLLAR, meshGroundNode). The ground
    // reaches a few centimetres into the hole (TerrainRenderer's HOLE_OVERLAP); a wall that
    // stopped below the ground left a slot between the two that grazing views saw straight
    // through. The collar standing above the ground closes it, and it doesn't show: at the
    // rim the rock has the ground's own palette and light.
    If(A.r.lessThan(A.drawn.sub(RIM_COLLAR)).or(B.r.lessThan(B.drawn.sub(RIM_COLLAR))), () => {
      Discard();
    });
    return vec4(color, 1);
  })();
  const emissive: N = lichen
    .add(bounce)
    .add(albedo.mul(leak).mul(0.35))
    .add(surface.mul(daylight).mul(1 / Math.PI))
    // Glowing ground (moss, embers…) glows on where its palette runs into the cave.
    .add(land(a, ctxA, wA, 'emissive').mul(wA))
    .add(land(b, ctxB, wB, 'emissive').mul(wB));
  mat.emissiveNode = deep ? mix(emissive, deep.emissive, inner) : emissive;
  return mat;
}
